import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authService, orgService } from "@/services/api";

const UIContext = createContext(null);

export function UIProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem("arc_theme") || "dark");
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("arc_sidebar") === "1");
  const [facilityId, setFacilityId] = useState("all");
  const [facilities, setFacilities] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const refetchFacilities = useCallback(() => {
    if (!authService.current()) return Promise.resolve();
    return orgService.facilities().then(setFacilities).catch(() => {});
  }, []);

  useEffect(() => {
    // Guarded on a session existing — this provider wraps /login too, and
    // firing an authenticated call there would just 401 for no reason.
    refetchFacilities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    localStorage.setItem("arc_theme", theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem("arc_sidebar", collapsed ? "1" : "0");
  }, [collapsed]);

  // Global keyboard shortcut for search (Cmd/Ctrl+K)
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const toggleTheme = useCallback(() => setTheme((t) => (t === "dark" ? "light" : "dark")), []);
  const toggleSidebar = useCallback(() => setCollapsed((c) => !c), []);

  const facilityLabel =
    facilityId === "all" ? "All Facilities" : facilities.find((f) => f.id === facilityId)?.name || "All Facilities";

  return (
    <UIContext.Provider
      value={{
        theme, setTheme, toggleTheme,
        collapsed, setCollapsed, toggleSidebar,
        facilityId, setFacilityId, facilityLabel, facilities, refetchFacilities,
        searchOpen, setSearchOpen,
        mobileNavOpen, setMobileNavOpen,
      }}
    >
      {children}
    </UIContext.Provider>
  );
}

export const useUI = () => useContext(UIContext);
